import type { FC } from 'react';
import { Box } from '@mui/material';
import type { PickersDayProps } from '@mui/x-date-pickers';
import { LocalizationProvider, DatePicker, PickersDay } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addDays, isMonday, isThursday, parseISO, subDays } from 'date-fns';
import { ArrowRightAlt } from '@mui/icons-material';
import useFacebookAdsLibraryParams, { type RegionType } from '../hooks/useFacebookAdsLibraryParams';
import { getLastWeekStart } from '../utils/getMondays';

const MIN_DATE_BY_REGION: Record<RegionType, Date> = {
  eu: parseISO('2025-10-13'),
  us: parseISO('2026-04-06'),
};

const weekStartDaySx = (isWeekStart: boolean) =>
  isWeekStart
    ? {
        backgroundColor: '#e0f7fa',
        borderRadius: '50%',
        border: '1px solid #00acc1',
        '&:hover': {
          backgroundColor: '#b2ebf2',
        },
        '&.Mui-selected': {
          backgroundColor: '#00acc1',
          color: 'white',
          '&:hover': {
            backgroundColor: '#00838f',
          },
        },
      }
    : {
        // Optional: Make non-week-start days look more obviously disabled
        opacity: 0.5,
      };

function MondayPickerDay(props: PickersDayProps<Date>) {
  const { day, ...other } = props;

  return <PickersDay {...other} day={day} sx={weekStartDaySx(isMonday(day))} />;
}

function ThursdayPickerDay(props: PickersDayProps<Date>) {
  const { day, ...other } = props;

  return <PickersDay {...other} day={day} sx={weekStartDaySx(isThursday(day))} />;
}

const MondaysDatePickers: FC = () => {
  const { startDate, endDate, setEndDate, setStartDate, region, network } = useFacebookAdsLibraryParams();
  const isUs = region === 'us';
  // Google Ads Library is US-only and uses Thursday–Thursday weeks.
  const useThursdayWeek = network === 'google';
  const WeekStartPickerDay = useThursdayWeek ? ThursdayPickerDay : MondayPickerDay;

  const shouldDisableDate = (date: Date) => {
    return useThursdayWeek ? !isThursday(date) : !isMonday(date);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DatePicker
          label="Start Week"
          value={startDate}
          slots={{ day: WeekStartPickerDay }}
          onChange={(v) => {
            setStartDate(v);
          }}
          format="dd-MM-yyyy"
          disableFuture
          shouldDisableDate={shouldDisableDate}
          maxDate={(endDate as Date | null) ? subDays(endDate, 1) : undefined}
          minDate={MIN_DATE_BY_REGION[region]}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 180 },
            },
          }}
        />

        {/* Visual Connector */}
        <ArrowRightAlt sx={{ color: 'action.active' }} />

        <DatePicker
          label="End Week"
          value={endDate}
          slots={{ day: WeekStartPickerDay }}
          onChange={(v) => {
            setEndDate(v);
          }}
          format="dd-MM-yyyy"
          shouldDisableDate={shouldDisableDate}
          minDate={(startDate as Date | null) ? addDays(startDate, 1) : undefined}
          maxDate={getLastWeekStart(useThursdayWeek)}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 180 },
            },
          }}
          disabled={isUs}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default MondaysDatePickers;
